import { Component, Input, forwardRef, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ITimeOfDay } from './time-input';

interface CalendarCell {
  day: number;
  date: Date;
  currentMonth: boolean;
  selected: boolean;
  today: boolean;
}

const POPUP_WIDTH = 300;
const POPUP_HEIGHT = 330;

/**
 * Date and time as two fields (like Outlook): a calendar for the date and a
 * time field that lists every half hour but also accepts a typed time such as
 * 1:03 PM. The form control value is a single Date.
 */
@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true
    },
    DatePipe
  ]
})
export class DatetimePickerComponent implements ControlValueAccessor, OnDestroy {
  @Input() label = 'Date';
  @Input() timeLabel = 'Time';
  @Input() disabled = false;

  @ViewChild('trigger') triggerRef: ElementRef<HTMLElement>;

  selectedDate: Date | null = null;
  dateDisplay = '';
  // Kept as one object per value so the time field only refreshes when the time changes.
  selectedTime: ITimeOfDay | null = null;
  showPopup = false;
  popupStyle: { top: string; left: string } = { top: '0', left: '0' };

  viewDate = new Date();
  monthYearLabel = '';
  weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  calendarCells: CalendarCell[] = [];

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};
  private backdropListener: (() => void) | null = null;

  constructor(private datePipe: DatePipe, private elRef: ElementRef) {}

  ngOnDestroy(): void {
    this.removeBackdropListener();
  }

  // ControlValueAccessor

  writeValue(value: Date | null): void {
    this.selectedDate = value ? new Date(value) : null;
    if (this.selectedDate) {
      this.viewDate = new Date(this.selectedDate);
    }
    this.updateDisplay();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Date

  toggleCalendar(): void {
    if (this.disabled) return;
    this.showPopup = !this.showPopup;
    if (!this.showPopup) {
      this.removeBackdropListener();
      return;
    }

    this.viewDate = new Date(this.selectedDate ?? new Date());
    this.buildCalendar();
    // Fixed position so the popup is not clipped by the dialog.
    setTimeout(() => {
      const rect = this.triggerRef?.nativeElement.getBoundingClientRect();
      if (!rect) return;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - POPUP_WIDTH - 8);
      const below = rect.bottom + 4;
      const top = below + POPUP_HEIGHT > window.innerHeight ? rect.top - POPUP_HEIGHT - 4 : below;
      this.popupStyle = { top: `${top}px`, left: `${left}px` };
    });
    this.addBackdropListener();
  }

  prevMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.buildCalendar();
  }

  nextMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.buildCalendar();
  }

  selectDay(cell: CalendarCell): void {
    const time = this.currentTime() ?? this.defaultTime();
    this.selectedDate = new Date(
      cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate(), time.hours, time.minutes
    );
    this.showPopup = false;
    this.removeBackdropListener();
    this.emitValue();
  }

  buildCalendar(): void {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    this.monthYearLabel = this.datePipe.transform(this.viewDate, 'MMMM yyyy') || '';

    const today = new Date();
    const firstCell = new Date(year, month, 1 - new Date(year, month, 1).getDay());
    this.calendarCells = Array.from({ length: 42 }, (_, i) => {
      const date = new Date(firstCell.getFullYear(), firstCell.getMonth(), firstCell.getDate() + i);
      return {
        day: date.getDate(),
        date,
        currentMonth: date.getMonth() === month,
        selected: this.isSameDay(date, this.selectedDate),
        today: this.isSameDay(date, today)
      };
    });
  }

  // Time

  onTimeChange(time: ITimeOfDay): void {
    const date = this.selectedDate ?? new Date();
    this.selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.hours, time.minutes);
    this.emitValue();
  }

  // Helpers

  private emitValue(): void {
    this.updateDisplay();
    this.onChange(this.selectedDate ? new Date(this.selectedDate) : null);
    this.onTouched();
  }

  private updateDisplay(): void {
    this.dateDisplay = this.selectedDate ? this.datePipe.transform(this.selectedDate, 'MMM d, yyyy') || '' : '';
    this.selectedTime = this.currentTime();
  }

  private currentTime(): ITimeOfDay | null {
    return this.selectedDate
      ? { hours: this.selectedDate.getHours(), minutes: this.selectedDate.getMinutes() }
      : null;
  }

  // No time yet: the next full hour.
  private defaultTime(): ITimeOfDay {
    const nextHour = (new Date().getHours() + 1) % 24;
    return { hours: nextHour, minutes: 0 };
  }

  private addBackdropListener(): void {
    this.removeBackdropListener();
    const handler = (event: MouseEvent) => {
      if (!this.elRef.nativeElement.contains(event.target) &&
          !(event.target as HTMLElement).closest('.dtp-popup-container')) {
        this.showPopup = false;
        this.onTouched();
        this.removeBackdropListener();
      }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', handler, true);
      this.backdropListener = () => document.removeEventListener('mousedown', handler, true);
    });
  }

  private removeBackdropListener(): void {
    if (this.backdropListener) {
      this.backdropListener();
      this.backdropListener = null;
    }
  }

  private isSameDay(a: Date, b: Date | null): boolean {
    if (!b) return false;
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
  }
}
